import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACTION_TYPES,
  getLogs,
  exportDailyLog,
  exportFilteredLogs,
  syncOfflineLogs,
  getOfflineQueueCount,
} from '../../../services/auditLogService';
import './AuditLogManager.css';

const AuditLogManager = () => {
  const { t, i18n } = useTranslation();

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedActionTypes, setSelectedActionTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Data & UI state
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('api');
  const [offlineCount, setOfflineCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  // Action dropdown
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const actionDropdownRef = useRef(null);

  // Stats
  const successCount = logs.filter(l => l.status === 'success').length;
  const errorCount = logs.filter(l => l.status === 'error').length;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(e.target)) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load offline count on mount and after each fetch
  const refreshOfflineCount = useCallback(async () => {
    try {
      const count = await getOfflineQueueCount();
      setOfflineCount(count);
    } catch {
      setOfflineCount(0);
    }
  }, []);

  useEffect(() => {
    refreshOfflineCount();
  }, [refreshOfflineCount]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshOfflineCount();
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshOfflineCount]);

  // Fetch logs
  const fetchLogs = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const filters = {
        page,
        perPage,
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(selectedActionTypes.length ? { actionType: selectedActionTypes } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(searchQuery ? { search: searchQuery } : {}),
      };
      const result = await getLogs(filters);
      setLogs(result.logs || []);
      setTotal(result.total || 0);
      setCurrentPage(result.currentPage || 1);
      setLastPage(result.lastPage || 1);
      setSource(result.source || 'indexeddb');
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, selectedActionTypes, statusFilter, searchQuery, perPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLogs(1);
  }, [fetchLogs]);

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    setCurrentPage(page);
    fetchLogs(page);
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedActionTypes([]);
    setStatusFilter('');
    setSearchQuery('');
    setPerPage(25);
    setCurrentPage(1);
  };

  const handleActionToggle = (key) => {
    setSelectedActionTypes(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleExportDay = async () => {
    const date = dateFrom || new Date().toISOString().split('T')[0];
    const result = await exportDailyLog(date);
    if (!result.success) {
      setError(result.error);
      setTimeout(() => setError(null), 4000);
    }
  };

  const handleExportFiltered = async () => {
    const result = await exportFilteredLogs({
      dateFrom,
      dateTo,
      actionType: selectedActionTypes,
      status: statusFilter,
      search: searchQuery,
    });
    if (!result.success) {
      setError(result.error);
      setTimeout(() => setError(null), 4000);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await syncOfflineLogs();
      if (result.success) {
        setOfflineCount(result.remaining);
        fetchLogs(currentPage);
      }
    } catch {
      // ignore
    } finally {
      setIsSyncing(false);
    }
  };

  const hasFilters = dateFrom || dateTo || selectedActionTypes.length || statusFilter || searchQuery;

  const actionTypeList = Object.values(ACTION_TYPES);

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionBadgeClass = (category) => {
    switch (category) {
      case 'user': return 'badge-user';
      case 'post': return 'badge-post';
      case 'clinic': return 'badge-clinic';
      case 'system': return 'badge-system';
      case 'cms': return 'badge-cms';
      case 'pet': return 'badge-pet';
      default: return 'badge-default';
    }
  };

  const startItem = (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, total);

  return (
    <div className="audit-log-manager">
      {/* Header */}
      <div className="composer-header">
        <h3>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          {t('auditLog.title', 'Audit Logs')}
        </h3>
        <p>{t('auditLog.description', 'Track every action taken by admins across the platform.')}</p>
      </div>

      {/* Sync & Source indicator */}
      <div className="audit-log-meta-bar">
        <div className="audit-log-source">
          {source === 'indexeddb' && (
            <span className="source-badge source-offline">
              {t('auditLog.offlineMode', 'Local Storage')}
            </span>
          )}
          {(source === 'api' || source === 'merged') && (
            <span className="source-badge source-online">
              {t('auditLog.onlineMode', 'Live')}
            </span>
          )}
        </div>
        {offlineCount > 0 && (
          <div className="audit-log-sync">
            <button
              className="sync-btn"
              onClick={handleSync}
              disabled={isSyncing}
              title={t('auditLog.syncNow', 'Sync now')}
            >
              {isSyncing ? (
                <span className="loading-spinner-small"></span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
              )}
              {t('auditLog.syncNow', 'Sync now')}
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="composer-alert alert-error">
          {error}
          <button className="alert-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Filters */}
      <div className="audit-log-filters">
        <div className="filter-group">
          <label>{t('auditLog.dateFrom', 'From')}</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>{t('auditLog.dateTo', 'To')}</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <div className="filter-group" ref={actionDropdownRef}>
          <label>{t('auditLog.actionType', 'Action')}</label>
          <button
            className="filter-dropdown-trigger"
            onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
          >
            {selectedActionTypes.length === 0
              ? t('auditLog.allActions', 'All actions')
              : selectedActionTypes.length === 1
                ? ACTION_TYPES[selectedActionTypes[0]]?.label || selectedActionTypes[0]
                : t('auditLog.selectedCount', '{{count}} selected', { count: selectedActionTypes.length })}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {actionDropdownOpen && (
            <div className="filter-dropdown-menu">
              {actionTypeList.map(action => (
                <label key={action.key} className="filter-dropdown-item">
                  <input
                    type="checkbox"
                    checked={selectedActionTypes.includes(action.key)}
                    onChange={() => handleActionToggle(action.key)}
                  />
                  <span className={`action-badge-mini ${getActionBadgeClass(action.category)}`}>
                    {action.label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="filter-group">
          <label>{t('auditLog.status', 'Result')}</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('auditLog.allResults', 'All results')}</option>
            <option value="success">{t('auditLog.success', 'Success')}</option>
            <option value="error">{t('auditLog.failed', 'Failed')}</option>
          </select>
        </div>

        <div className="filter-group filter-search">
          <label>{t('auditLog.search', 'Search')}</label>
          <div className="filter-search-input">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={t('auditLog.searchPlaceholder', 'Search by name, email, or ID...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
        </div>

        {hasFilters && (
          <button className="filter-clear-btn" onClick={handleClearFilters}>
            {t('auditLog.clearFilters', 'Clear')}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="audit-log-stats">
        <div className="stat-box">
          <span className="stat-value">{total}</span>
          <span className="stat-label">{t('auditLog.totalLogs', 'Total')}</span>
        </div>
        <div className="stat-box stat-success">
          <span className="stat-value">{successCount}</span>
          <span className="stat-label">{t('auditLog.successCount', 'Success')}</span>
        </div>
        <div className="stat-box stat-error">
          <span className="stat-value">{errorCount}</span>
          <span className="stat-label">{t('auditLog.errorCount', 'Failed')}</span>
        </div>
      </div>

      {/* Export buttons */}
      <div className="audit-log-export-bar">
        <button className="btn-export" onClick={handleExportDay}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          {t('auditLog.exportDay', 'Export Day')}
        </button>
        <button className="btn-export" onClick={handleExportFiltered}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          {t('auditLog.exportFiltered', 'Export Filtered')}
        </button>
      </div>

      {/* Table */}
      <div className="audit-log-table-wrap">
        {isLoading ? (
          <div className="audit-log-loading">
            <div className="loading-spinner"></div>
            <p>{t('auditLog.loading', 'Loading audit logs...')}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="audit-log-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            <p>{t('auditLog.noLogs', 'No audit logs found.')}</p>
            {hasFilters && (
              <button className="btn-link" onClick={handleClearFilters}>
                {t('auditLog.clearFilters', 'Clear filters')}
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="audit-log-table">
              <thead>
                <tr>
                  <th>{t('auditLog.dateTime', 'Date & Time')}</th>
                  <th>{t('auditLog.action', 'Action')}</th>
                  <th>{t('auditLog.admin', 'Admin')}</th>
                  <th>{t('auditLog.target', 'Target')}</th>
                  <th>{t('auditLog.whatHappened', 'What Happened')}</th>
                  <th>{t('auditLog.result', 'Result')}</th>
                  <th>{t('auditLog.duration', 'Time Taken')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={log.id || idx} className={log.status === 'error' ? 'row-error' : ''}>
                    <td className="cell-datetime">{formatDateTime(log.timestamp)}</td>
                    <td className="cell-action">
                      <span className={`action-badge ${getActionBadgeClass(log.category)}`}>
                        {log.actionLabel || log.actionType}
                      </span>
                    </td>
                    <td className="cell-admin">
                      <div className="admin-info">
                        <span className="admin-name">{log.adminName || '—'}</span>
                        {log.adminEmail && <span className="admin-email">{log.adminEmail}</span>}
                      </div>
                    </td>
                    <td className="cell-target">
                      {(() => {
                        // No target at all
                        if (!log.targetId && !log.targetName) return <span className="target-name">—</span>;
                        // Has a real name
                        if (log.targetName && log.targetName !== 'Unknown' && log.targetName !== 'a target') {
                          return (
                            <>
                              <span className="target-name">{log.targetName}</span>
                              {log.targetId && <span className="target-id">#{log.targetId}</span>}
                            </>
                          );
                        }
                        // Only has ID — show type + ID
                        const typeLabel = log.targetType && log.targetType !== 'unknown'
                          ? log.targetType.charAt(0).toUpperCase() + log.targetType.slice(1)
                          : 'Item';
                        return <span className="target-name">{typeLabel} #{log.targetId}</span>;
                      })()}
                    </td>
                    <td className="cell-summary">
                      <span className="summary-text" title={log.summary}>
                        {log.summary || '—'}
                      </span>
                      {log.errorMessage && (
                        <span className="error-detail" title={log.errorMessage}>
                          {log.errorMessage}
                        </span>
                      )}
                    </td>
                    <td className="cell-status">
                      {log.status === 'success' ? (
                        <span className="status-badge status-success">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          {t('auditLog.success', 'Success')}
                        </span>
                      ) : (
                        <span className="status-badge status-error">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                          {t('auditLog.failed', 'Failed')}
                        </span>
                      )}
                    </td>
                    <td className="cell-duration">
                      {log.durationMs ? `${log.durationMs}ms` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="audit-log-pagination-wrap">
              <div className="audit-log-perpage">
                <label>{t('auditLog.perPage', 'Per page')}</label>
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="audit-log-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  {t('auditLog.prev', 'Previous')}
                </button>
                <span>
                  {t('auditLog.showing', 'Showing {{start}}–{{end}} of {{total}}', {
                    start: startItem,
                    end: endItem,
                    total,
                  })}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                >
                  {t('auditLog.next', 'Next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogManager;
